import { Component, ErrorInfo, ReactNode } from 'react';
import { theme } from '../constants';
import { utils } from '../utils';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <div
        style={{
          minHeight: '100vh',
          width: '100%',
          padding: 24,
          background: theme.colors.backgroundColour,
          color: theme.colors.mainDark,
          ...utils.columnCenter(),
          textAlign: 'center',
          ...theme.fonts.Poppins_400Regular,
        }}
      >
        <div
          style={{
            fontSize: 22,
            marginBottom: 12,
            ...theme.fonts.Poppins_600SemiBold,
          }}
        >
          Something went wrong
        </div>
        <div
          style={{
            fontSize: 14,
            maxWidth: 320,
            marginBottom: 24,
            color: theme.colors.bodyTextColor,
          }}
        >
          An unexpected error occurred. Please reload the app to try again.
        </div>
        <button
          onClick={this.handleReload}
          style={{
            height: 40,
            padding: '0 24px',
            borderRadius: 36,
            border: 'none',
            background: theme.colors.mainDark,
            color: theme.colors.white,
            fontSize: 16,
            cursor: 'pointer',
            ...theme.fonts.Poppins_600SemiBold,
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
